import {
  ISensorFusionStageDefinitionLookupDto,
  ISensorFusionStageInstanceLookupDto,
  ISensorFusionSubstageDefinitionLookupDto,
  ISensorFusionSubstageInstanceLookupDto,
  ISensorFusionUnifiedProjectLookupDto,
  ISensorFusionUnifiedTaskLookupDto,
} from '@scale/sensor-fusion-shared';
import {
  CommonPipelineNodeName,
  StageInstanceStatus,
  SubstageInstanceStatus,
  TaskType,
} from '@scale/shared-types';
import { TaskStatus, ReviewLevel, AnnotationAttributeType } from '@scale/shared/defaults';
import { PipelineV3HumanNodeStatus } from '@scale/shared/pipelinev3/humanNodeStatus';

export const CORP_LOOKUP_PATH = 'corp/lookup';
export const REMOADMIN_LOOKUP_PATH = 'remoadmin/tools/lookup';
export const QM_PROJECT_LOOKUP_PATH = 'corp/qm/queue';

export const LIDARLITE_DEBUG_TASK_PATH = 'lidarlite/?entrypoint=debug&taskId=';

export const CORP_SUBSTAGES_PATH = 'corp/substages';
export const REMOADMIN_SUBSTAGES_PATH = 'remoadmin/tools/substages';

// tools
export const COMPLETE_SUBSTAGES_CORP_TOOL_PATH = 'corp/sfu_complete_substages';
export const BATCH_ACTIONS_CORP_TOOL_PATH = 'corp/batch_actions';

export const DOUBLE_COLUMN_MIN_WIDTH = 1300;

export const EXAMPLE_STAGE_DEFINITION: ISensorFusionStageDefinitionLookupDto = {
  _id: '123',
  __t: 'sensorfusionstagedefinition',
  srn: 'srn:123',
  name: 'Object Detection Stage',
  customer: {
    id: '567',
    company: 'Anteater',
    email: 'example@anteater.com',
  },
  project: {
    id: '93',
    name: '[Anteater] Unified Sensor Fusion',
  },
  childProject: {
    id: '9393',
    name: '[Anteater] Unified Sensor Fusion - child',
    permissionGroup: {
      id: '419',
      name: 'Anteater Permission Group',
    },
  },
  substageDefinitions: [
    {
      id: '41',
      name: '[OD] Cuboids + Some attributes Substage',
      statusCounts: {
        claimed: {
          [SubstageInstanceStatus.NotStarted]: 0,
          [SubstageInstanceStatus.InProgress]: 2,
          [SubstageInstanceStatus.Completed]: 0,
          [SubstageInstanceStatus.Quarantined]: 1,
          [SubstageInstanceStatus.Canceled]: 0,
        },
        unclaimed: {
          [SubstageInstanceStatus.NotStarted]: 0,
          [SubstageInstanceStatus.InProgress]: 1,
          [SubstageInstanceStatus.Completed]: 1,
          [SubstageInstanceStatus.Quarantined]: 0,
          [SubstageInstanceStatus.Canceled]: 1,
        },
      },
      childTaskType: 'sensorfusion',
    },
    {
      id: '42',
      name: '[OD] Rest of attributes Substage',
      statusCounts: {
        claimed: {
          [SubstageInstanceStatus.NotStarted]: 2,
          [SubstageInstanceStatus.InProgress]: 3,
          [SubstageInstanceStatus.Completed]: 1,
          [SubstageInstanceStatus.Quarantined]: 0,
          [SubstageInstanceStatus.Canceled]: 1,
        },
        unclaimed: {
          [SubstageInstanceStatus.NotStarted]: 2,
          [SubstageInstanceStatus.InProgress]: 1,
          [SubstageInstanceStatus.Completed]: 0,
          [SubstageInstanceStatus.Quarantined]: 1,
          [SubstageInstanceStatus.Canceled]: 0,
        },
      },
      childTaskType: 'sensorfusion',
    },
  ],
  taxonomy: {
    name: 'OD Subtaxonomy',
    id: '1234',
  },
  createdAt: '2023-08-23T10:55:04.436Z',
};

export const EXAMPLE_STAGE_INSTANCE: ISensorFusionStageInstanceLookupDto = {
  _id: '1030',
  __t: 'sensorfusionstageinstance',
  srn: 'srn:123',
  createdAt: '2023-08-23T10:55:04.436Z',
  project: {
    id: '93',
    name: '[Anteater] Unified Sensor Fusion',
  },
  stageDefinition: {
    id: '6001',
    name: '[OD] Stage Definition',
  },
  params: {
    sceneId: '123',
  },
  responses: [
    {
      substageInstance: {
        defName: '[OD] Cuboids + Some attributes Substage Instance',
        defId: '1234',
        srn: 'srn:123',
        id: '1234',
        childTaskType: 'sensorfusion',
      },
      url: 'https://www.example.com',
    },
    {
      substageInstance: {
        defName: '[OD] Rest of attributes Substage Instance',
        defId: '1234',
        srn: 'srn:345',
        id: '1234',
        childTaskType: 'sensorfusion',
      },
      url: 'https://www.example.com',
    },
  ],
  substageInstances: [
    {
      id: '1234',
      srn: '1234',
      defId: '1234',
      defName: '[OD] Cuboids + Some attributes Substage Instance',
      childTaskType: 'sensorfusion',
      status: SubstageInstanceStatus.Completed,
    },
    {
      id: '1234',
      srn: '1235',
      defId: '1234',
      defName: '[OD] Rest of attributes Substage Instance',
      childTaskType: 'sensorfusion',
      status: SubstageInstanceStatus.Completed,
    },
  ],
  taskId: '63b16dcb1ef47a00241cabc2',
  status: StageInstanceStatus.Pending,
};

export const EXAMPLE_SUBSTAGE_DEFINITION: ISensorFusionSubstageDefinitionLookupDto = {
  _id: '41',
  __t: 'sensorfusionsubstagedefinition',
  srn: 'srn:123',
  name: '[OD] Cuboids + Some attributes Substage',
  customer: {
    id: '567',
    company: 'Anteater',
    email: 'example@anteater.com',
  },
  project: {
    id: '93',
    name: '[Anteater] Unified Sensor Fusion',
  },
  childProject: {
    id: '9901',
    name: '[Anteater] Unified Sensor Fusion - child',
    permissionGroup: {
      id: '419',
      name: 'Anteater Permission Group',
    },
  },
  stageDefinition: {
    id: '123',
    name: 'Object Detection Stage',
  },
  statusCounts: {
    claimed: {
      [SubstageInstanceStatus.NotStarted]: 4,
      [SubstageInstanceStatus.InProgress]: 4,
      [SubstageInstanceStatus.Completed]: 4,
      [SubstageInstanceStatus.Quarantined]: 1,
      [SubstageInstanceStatus.Canceled]: 0,
    },
    unclaimed: {
      [SubstageInstanceStatus.NotStarted]: 5,
      [SubstageInstanceStatus.InProgress]: 4,
      [SubstageInstanceStatus.Completed]: 3,
      [SubstageInstanceStatus.Quarantined]: 0,
      [SubstageInstanceStatus.Canceled]: 1,
    },
  },
  taxonomy: {
    name: '[OD] Cuboids + Some attributes Taxonomy',
    id: '1234',
  },
  childTaskType: 'sensorfusion',
  createdAt: '2023-08-23T10:55:04.436Z',
};

export const EXAMPLE_SUBSTAGE_INSTANCE: ISensorFusionSubstageInstanceLookupDto = {
  _id: '2041',
  srn: '2041',
  __t: 'sensorfusionsubstageinstance',
  createdAt: '2023-08-23T10:55:04.436Z',
  substageDefinition: {
    id: '5001',
    name: '[OD] Cuboids + Some attributes Substage Definition',
    childTaskType: 'sensorfusion',
  },
  project: {
    id: '93',
    name: '[Anteater] Unified Sensor Fusion',
  },
  params: {
    sceneId: '123',
  },
  responses: [
    {
      url: 'https://www.example.com',
    },
    {
      url: 'https://www.example.com',
    },
  ],
  stageInstance: {
    id: '1030',
    defId: '1029',
    defName: '[OD] Stage Instance',
  },
  humanNode: {
    id: '1818',
    status: PipelineV3HumanNodeStatus.Pending,
  },
  status: SubstageInstanceStatus.Completed,
  taskId: '63b16dcb1ef47a00241cabc2',
  submissionLog: [
    {
      taskAttempt: '1234',
      humanNode: '1234',
      user: '1234',
      reviewLevel: ReviewLevel.Attempt,
      reviewOutcome: 'fixed',
      submissionTime: new Date(),
      timeSpent: 10,
      activeTimeSpent: 5,
    },
  ],
  taxonomy: {
    cuboid: {
      type: 'cuboid',
      labels: ['Vertical Structure'],
      attributes: [
        {
          key: 'Type',
          type: AnnotationAttributeType.Category,
          description: 'What type of vertical structure is it?',
          choices: ['Pole', 'HorizontalStructurePole'],
          global: true,
          conditions: {
            label_condition: {
              label: ['Vertical Structure'],
            },
          },
        },
        {
          key: 'NondescriptVerticalStructure',
          type: AnnotationAttributeType.Category,
          description:
            'A vertical structure that is considered to be a NonDescript object shall have one of these values set',
          choices: ['Broken', 'Tilted', 'Other', 'None'],
          global: true,
          conditions: {
            label_condition: {
              label: ['Vertical Structure'],
            },
          },
        },
        {
          key: 'HasLightSource',
          type: AnnotationAttributeType.Category,
          description: 'Does the object have any light source attached to it?',
          choices: ['True', 'False'],
          global: true,
          conditions: {
            label_condition: {
              label: ['Vertical Structure'],
            },
            attribute_conditions: [
              {
                NondescriptVerticalStructure: 'None',
              },
            ],
          },
        },
        {
          key: 'HasSign',
          type: AnnotationAttributeType.Category,
          description: 'Does the object have a sign attached to it?',
          choices: ['True', 'False'],
          global: true,
          conditions: {
            label_condition: {
              label: ['Vertical Structure'],
            },
            attribute_conditions: [
              {
                NondescriptVerticalStructure: 'None',
              },
            ],
          },
        },
        {
          key: 'HasTrafficLight',
          type: AnnotationAttributeType.Category,
          description: 'Does the object have a traffic light?',
          choices: ['True', 'False'],
          global: true,
          conditions: {
            label_condition: {
              label: ['Vertical Structure'],
            },
            attribute_conditions: [
              {
                NondescriptVerticalStructure: 'None',
              },
            ],
          },
        },
        {
          key: 'HasCamera',
          type: AnnotationAttributeType.Category,
          description: 'Does the object have a camera?',
          choices: ['True', 'False'],
          global: true,
          conditions: {
            label_condition: {
              label: ['Vertical Structure'],
            },
            attribute_conditions: [
              {
                NondescriptVerticalStructure: 'None',
              },
            ],
          },
        },
        {
          key: 'HasUtility',
          type: AnnotationAttributeType.Category,
          description: 'Does the object something else attached to it?',
          choices: ['True', 'False'],
          global: true,
          conditions: {
            label_condition: {
              label: ['Vertical Structure'],
            },
            attribute_conditions: [
              {
                NondescriptVerticalStructure: 'None',
              },
            ],
          },
        },
        {
          key: 'HasOther',
          type: AnnotationAttributeType.Category,
          description: 'Does the object something else attached to it?',
          choices: ['True', 'False'],
          global: true,
          conditions: {
            label_condition: {
              label: ['Vertical Structure'],
            },
            attribute_conditions: [
              {
                NondescriptVerticalStructure: 'None',
              },
            ],
          },
        },
        {
          key: 'IsSolid',
          type: AnnotationAttributeType.Category,
          description: 'Is the vertical structure solid?',
          choices: ['True', 'False'],
          global: true,
          conditions: {
            label_condition: {
              label: ['Vertical Structure'],
            },
            attribute_conditions: [
              {
                NondescriptVerticalStructure: 'None',
              },
            ],
          },
        },
        {
          key: 'IsCurved',
          type: AnnotationAttributeType.Category,
          description: 'Is the vertical structure curved?',
          choices: ['True', 'False'],
          global: true,
          conditions: {
            label_condition: {
              label: ['Vertical Structure'],
            },
            attribute_conditions: [
              {
                NondescriptVerticalStructure: 'None',
              },
            ],
          },
        },
      ],
      locked_labels: ['Vertical Structure'],
    },
  },
};

export const EXAMPLE_SFU_PROJECT: ISensorFusionUnifiedProjectLookupDto = {
  _id: '93',
  __t: 'sensorfusionunifiedproject',
  name: '[Anteater] Unified Sensor Fusion',
  customer: {
    id: '567',
    company: 'Anteater',
    email: 'example@anteater.com',
  },
  taskType: TaskType.SensorFusion,
  taskCustomer: { _id: '567' },
  permissionGroup: {
    id: '419',
    name: 'Anteater Permission Group',
  },
  stageDefinitions: [
    {
      childProjectId: '9393',
      id: '123',
      name: 'Object Detection Stage',
      statusCounts: {
        claimed: {
          [SubstageInstanceStatus.NotStarted]: 1,
          [SubstageInstanceStatus.InProgress]: 1,
          [SubstageInstanceStatus.Completed]: 1,
          [SubstageInstanceStatus.Quarantined]: 1,
          [SubstageInstanceStatus.Canceled]: 0,
        },
        unclaimed: {
          [SubstageInstanceStatus.NotStarted]: 0,
          [SubstageInstanceStatus.InProgress]: 1,
          [SubstageInstanceStatus.Completed]: 2,
          [SubstageInstanceStatus.Quarantined]: 0,
          [SubstageInstanceStatus.Canceled]: 1,
        },
      },
      substageDefinitions: [
        {
          id: '41',
          name: '[OD] Cuboids + Some attributes Substage',
          childTaskType: 'sensorfusion',
          dependencies: [],
        },
        {
          id: '42',
          name: '[OD] Rest of attributes Substage',
          childTaskType: 'sensorfusion',
          dependencies: [],
        },
      ],
      dependencies: [],
    },
    {
      id: '124',
      name: 'Construction Stage',
      childProjectId: '9393',
      statusCounts: {
        claimed: {
          [SubstageInstanceStatus.NotStarted]: 2,
          [SubstageInstanceStatus.InProgress]: 3,
          [SubstageInstanceStatus.Completed]: 3,
          [SubstageInstanceStatus.Quarantined]: 0,
          [SubstageInstanceStatus.Canceled]: 1,
        },
        unclaimed: {
          [SubstageInstanceStatus.NotStarted]: 3,
          [SubstageInstanceStatus.InProgress]: 3,
          [SubstageInstanceStatus.Completed]: 4,
          [SubstageInstanceStatus.Quarantined]: 1,
          [SubstageInstanceStatus.Canceled]: 0,
        },
      },
      substageDefinitions: [
        {
          id: '43',
          name: '[C] Cuboids + Some attributes Substage',
          childTaskType: 'sensorfusion',
          dependencies: [],
        },
        {
          id: '44',
          name: '[C] Rest of attributes Substage',
          childTaskType: 'sensorfusion',
          dependencies: [],
        },
      ],
      dependencies: [],
    },
  ],
  taxonomy: {
    name: 'Anteater Unified Taxonomy',
    id: '1234',
  },
  createdAt: '2023-08-23T10:55:04.436Z',
  batchOverview: {
    batchRows: [
      {
        batchInfo: { batchId: 'batch1', batchName: 'batch1', batchPriority: 10 },
        taskCount: 1,
        completedStagesCounts: { 41: 1 },
        completedTasksCount: 0,
        holdTasksCount: 0,
        batchPriority: 20,
      },
    ],
    stageDefinitionsInfo: [
      {
        stageDefinitionId: '657911104bd52ea6ec6dd7ce',
        stageDefinitionName: 'sensorfusion',
      },
    ],
  },
  isBlueprintsEnabled: false,
};

export const EXAMPLE_SFU_TASK: ISensorFusionUnifiedTaskLookupDto = {
  _id: '64e5e58876193065addaf2c5',
  __t: 'sensorfusionunifiedtask',
  createdAt: '2023-08-23T10:55:04.436Z',
  userId: '567',
  blueprint: {} as any,
  params: {
    sceneId: '123',
  },
  stageInstances: [
    {
      id: '1030',
      defId: '123',
      defName: '[OD] Stage Instance',
      status: StageInstanceStatus.Pending,
      substageInstances: [
        {
          id: '1234',
          srn: '2041',
          defId: '123',
          defName: '[OD] Cuboids + Some attributes Substage Instance',
          childTaskType: 'sensorfusion',
          status: SubstageInstanceStatus.Completed,
        },
        {
          id: '1234',
          srn: '2042',
          defId: '124',
          defName: '[OD] Rest of attributes Substage Instance',
          childTaskType: 'sensorfusion',
          status: SubstageInstanceStatus.Completed,
        },
      ],
    },
    {
      id: '1031',
      defId: '1234',
      defName: '[FS] Stage Instance',
      status: StageInstanceStatus.Pending,
      substageInstances: [
        {
          id: '1234',
          srn: '2043',
          defId: '125',
          defName: '[FS] Substage Instance',
          childTaskType: 'sensorfusion',
          status: SubstageInstanceStatus.Completed,
        },
      ],
    },
  ],
  pipelineExecutionId: '876',
  pipelineNodes: [
    {
      id: '87601',
      taskId: '64a7c5b05323267a6455fa6c',
      orchestrationId: '64a7c5b05323267a6455fa6c',
      type: 'task',
      status: 'pending',
      nodeName: CommonPipelineNodeName.LidarLinkingV2Node,
      scope: 'lidar_linking_stage_subpipeline_v1',
      inputHash: '47cbfd037ae4605d62fcd4d40ca8d8b5f8862b84',

      createdAt: '2023-08-24T10:55:04.436Z',
      updatedAt: '2023-08-24T10:55:04.436Z',
      input: {},
      output: {},

      pipelineExecutionIds: [],
      dependentIds: [],
      dependencyIds: [],
      resourceDependencyIds: [],
      generatedResourceIds: [],
      inputResourceIds: [],
      outputResourceIds: [],
    },
  ],
  project: {
    id: '93',
    name: '[Anteater] Unified Sensor Fusion',
  },
  status: TaskStatus.Completed,
  type: TaskType.SensorFusion,
  taxonomy: {
    cuboid: {
      type: 'cuboid',
      labels: ['Vertical Structure'],
      attributes: [
        {
          key: 'Type',
          type: AnnotationAttributeType.Category,
          description: 'What type of vertical structure is it?',
          choices: ['Pole', 'HorizontalStructurePole'],
          global: true,
          conditions: { label_condition: { label: ['Vertical Structure'] } },
        },
        {
          key: 'NondescriptVerticalStructure',
          type: AnnotationAttributeType.Category,
          description:
            'A vertical structure that is considered to be a NonDescript object shall have one of these values set',
          choices: ['Broken', 'Tilted', 'Other', 'None'],
          global: true,
          conditions: { label_condition: { label: ['Vertical Structure'] } },
        },
        {
          key: 'OcclusionLevel',
          type: AnnotationAttributeType.Category,
          description: 'How much is the object occluded?',
          choices: ['0', '10', '20', '30', '40', '50', '60', '70', '80', '90', '100'],
          by_camera: true,
          conditions: {
            label_condition: { label: ['Vertical Structure'] },
            attribute_conditions: [{ NondescriptVerticalStructure: 'None' }],
          },
        },
        {
          key: 'Relevance',
          type: AnnotationAttributeType.Category,
          description: 'Is the object relevant to the ego road?',
          choices: ['True', 'False'],
          conditions: {
            label_condition: { label: ['Vertical Structure'] },
            attribute_conditions: [{ NondescriptVerticalStructure: 'None' }],
          },
        },
        {
          key: 'HasLightSource',
          type: AnnotationAttributeType.Category,
          description: 'Does the object have any light source attached to it?',
          choices: ['True', 'False'],
          global: true,
          conditions: {
            label_condition: { label: ['Vertical Structure'] },
            attribute_conditions: [{ NondescriptVerticalStructure: 'None' }],
          },
        },
        {
          key: 'HasSign',
          type: AnnotationAttributeType.Category,
          description: 'Does the object have a sign attached to it?',
          choices: ['True', 'False'],
          global: true,
          conditions: {
            label_condition: { label: ['Vertical Structure'] },
            attribute_conditions: [{ NondescriptVerticalStructure: 'None' }],
          },
        },
        {
          key: 'HasTrafficLight',
          type: AnnotationAttributeType.Category,
          description: 'Does the object have a traffic light?',
          choices: ['True', 'False'],
          global: true,
          conditions: {
            label_condition: { label: ['Vertical Structure'] },
            attribute_conditions: [{ NondescriptVerticalStructure: 'None' }],
          },
        },
        {
          key: 'HasCamera',
          type: AnnotationAttributeType.Category,
          description: 'Does the object have a camera?',
          choices: ['True', 'False'],
          global: true,
          conditions: {
            label_condition: { label: ['Vertical Structure'] },
            attribute_conditions: [{ NondescriptVerticalStructure: 'None' }],
          },
        },
        {
          key: 'HasUtility',
          type: AnnotationAttributeType.Category,
          description: 'Does the object something else attached to it?',
          choices: ['True', 'False'],
          global: true,
          conditions: {
            label_condition: { label: ['Vertical Structure'] },
            attribute_conditions: [{ NondescriptVerticalStructure: 'None' }],
          },
        },
        {
          key: 'HasOther',
          type: AnnotationAttributeType.Category,
          description: 'Does the object something else attached to it?',
          choices: ['True', 'False'],
          global: true,
          conditions: {
            label_condition: { label: ['Vertical Structure'] },
            attribute_conditions: [{ NondescriptVerticalStructure: 'None' }],
          },
        },
        {
          key: 'IsSolid',
          type: AnnotationAttributeType.Category,
          description: 'Is the vertical structure solid?',
          choices: ['True', 'False'],
          global: true,
          conditions: {
            label_condition: { label: ['Vertical Structure'] },
            attribute_conditions: [{ NondescriptVerticalStructure: 'None' }],
          },
        },
        {
          key: 'IsCurved',
          type: AnnotationAttributeType.Category,
          description: 'Is the vertical structure curved?',
          choices: ['True', 'False'],
          global: true,
          conditions: {
            label_condition: { label: ['Vertical Structure'] },
            attribute_conditions: [{ NondescriptVerticalStructure: 'None' }],
          },
        },
        {
          key: 'TopOccluded',
          type: AnnotationAttributeType.Category,
          description: 'Is the Top 2D keypoint occluded in this camera?',
          choices: ['True', 'False'],
          by_camera: true,
          conditions: { label_condition: { label: ['Vertical Structure'] } },
        },
        {
          key: 'CenterOccluded',
          type: AnnotationAttributeType.Category,
          description: 'Is the Center 2D keypoint occluded in this camera?',
          choices: ['True', 'False'],
          by_camera: true,
          conditions: { label_condition: { label: ['Vertical Structure'] } },
        },
        {
          key: 'BottomOccluded',
          type: AnnotationAttributeType.Category,
          description: 'Is the Bottom 2D keypoint occluded in this camera?',
          choices: ['True', 'False'],
          by_camera: true,
          conditions: { label_condition: { label: ['Vertical Structure'] } },
        },
      ],
    },
    point_3d: {
      type: 'point_3d',
      labels: [
        'Top',
        'Center',
        'Bottom',
        'Width - Bottom outer left',
        'Width - Bottom outer right',
      ],
      projections: { sampling_options: { type: 'frame_rate', rate: 10 } },
    },
  },
};
